/**
 * The console's typed client for the admin Worker's own-origin /api/* BFF. Every
 * request rides the HttpOnly session cookie (credentials: 'same-origin'); the
 * browser never holds a token. A central interceptor turns any unexpected 401 into
 * a "session gone" signal so the app can bounce to /login.
 */

import type {
    AcknowledgeAlertResponse,
    AdminListRoomsResponse,
    AdminRoomDetailResponse,
    CalendarLink,
    CalendarProvider,
    ConnectCalendarResponse,
    Environment,
    HealthSummaryResponse,
    ListAccessibleCalendarsResponse,
    ListAlertsResponse,
    ListDevicesResponse,
    PreviewLinkResponse,
    ResetPairingCodeResponse,
    RoomHealthResponse,
    ScanResultResponse,
    Room
} from '@spaces/contracts';

/** A normalised result: success with data, or a structured failure. */
export type ApiResult<T> =
    | { ok: true; data: T }
    | { ok: false; code: string; message: string; status: number };

/** Session-state payload from GET /api/auth/session. */
export interface SessionState {
    authenticated: boolean;
    env: Environment;
    /** The tenant (8x8 cid) this session is scoped to — present when authenticated. */
    tenant?: string;
    /** Who the operator is (from their 8x8 token) — present when authenticated. */
    identity?: { loginId: string };
    capabilities?: { resetPairingCode: boolean };
}

let onUnauthorized: (() => void) | null = null;

/** Register the callback fired when a gated call returns 401 (session expired). */
export function setUnauthorizedHandler(fn: () => void): void {
    onUnauthorized = fn;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<ApiResult<T>> {
    let res: Response;

    try {
        res = await fetch(path, {
            method,
            credentials: 'same-origin',
            headers: body === undefined ? {} : { 'content-type': 'application/json' },
            body: body === undefined ? undefined : JSON.stringify(body)
        });
    } catch (e) {
        return { ok: false, code: 'network_error', message: (e as Error).message, status: 0 };
    }

    // Session expired mid-flight on a gated call -> let the app redirect to login.
    if (res.status === 401 && path !== '/api/auth/session' && onUnauthorized) {
        onUnauthorized();
    }

    let parsed: { ok?: boolean; data?: T; error?: { code?: string; message?: string } } | null;

    try {
        parsed = await res.json();
    } catch {
        parsed = null;
    }

    if (res.ok && parsed?.ok) {
        return { ok: true, data: parsed.data as T };
    }

    return {
        ok: false,
        code: parsed?.error?.code ?? 'error',
        message: parsed?.error?.message ?? `Request failed (${res.status})`,
        status: res.status
    };
}

// --- Auth -------------------------------------------------------------------

export function getSession(): Promise<ApiResult<SessionState>> {
    return request<SessionState>('GET', '/api/auth/session');
}

export function login(token: string): Promise<ApiResult<{ authenticated: boolean; tenant: string }>> {
    return request('POST', '/api/auth/login', { token });
}

export function logout(): Promise<ApiResult<{ authenticated: boolean }>> {
    return request('POST', '/api/auth/logout');
}

// --- Rooms ------------------------------------------------------------------

export function listRooms(tenant: string): Promise<ApiResult<AdminListRoomsResponse>> {
    return request<AdminListRoomsResponse>('GET', `/api/rooms?tenant=${encodeURIComponent(tenant)}`);
}

export function createRoom(input: { name: string; tenant: string; countryCode?: string }): Promise<ApiResult<Room>> {
    return request<Room>('POST', '/api/rooms', input);
}

export function getRoom(id: string): Promise<ApiResult<AdminRoomDetailResponse>> {
    return request<AdminRoomDetailResponse>('GET', `/api/rooms/${encodeURIComponent(id)}`);
}

export function updateRoom(
    id: string,
    patch: { name?: string; countryCode?: string; status?: Room['status'] }
): Promise<ApiResult<Room>> {
    return request<Room>('PATCH', `/api/rooms/${encodeURIComponent(id)}`, patch);
}

export function listDevices(id: string): Promise<ApiResult<ListDevicesResponse>> {
    return request<ListDevicesResponse>('GET', `/api/rooms/${encodeURIComponent(id)}/devices`);
}

export function resetPairingCode(id: string): Promise<ApiResult<ResetPairingCodeResponse>> {
    return request<ResetPairingCodeResponse>('POST', `/api/rooms/${encodeURIComponent(id)}/reset-pairing-code`);
}

export function getCalendarLink(id: string): Promise<ApiResult<CalendarLink>> {
    return request<CalendarLink>('GET', `/api/rooms/${encodeURIComponent(id)}/calendar/link`);
}

export function connectCalendar(id: string): Promise<ApiResult<ConnectCalendarResponse>> {
    return request<ConnectCalendarResponse>('POST', `/api/rooms/${encodeURIComponent(id)}/calendar/connect`);
}

/** `GET /api/rooms/:id/calendar/accessible` -> resource calendars the operator can bind. */
export function listAccessibleCalendars(id: string): Promise<ApiResult<ListAccessibleCalendarsResponse>> {
    return request<ListAccessibleCalendarsResponse>('GET', `/api/rooms/${encodeURIComponent(id)}/calendar/accessible`);
}

/** `PUT /api/rooms/:id/calendar/link` -> bind the room to a provider calendar by id. */
export function setRoomCalendar(
    id: string,
    externalCalendarId: string,
    provider?: CalendarProvider
): Promise<ApiResult<CalendarLink>> {
    return request<CalendarLink>('PUT', `/api/rooms/${encodeURIComponent(id)}/calendar/link`, { externalCalendarId, provider });
}

/** `DELETE /api/rooms/:id/calendar/link` -> unbind the room's calendar (returns the unlinked state). */
export function unlinkRoomCalendar(id: string): Promise<ApiResult<CalendarLink>> {
    return request<CalendarLink>('DELETE', `/api/rooms/${encodeURIComponent(id)}/calendar/link`);
}

// --- Org-room calendar consent (JIT-15819) ----------------------------------
// Customer-level: whether this org has granted 8x8 calendar delegation, and the grant flow.
// Scoped server-side to the operator's tenant/cid (the BFF forwards it) — no id needed here.

/** The org's calendar-delegation enrollment state (as rosy reports it via the BFF). */
export interface OrgCalendarStatus {
    enrolled: boolean;
    provider: 'google' | 'microsoft' | null;
    status: 'none' | 'active' | 'needs_reauth' | 'revoked';
}

/** `GET /api/calendars/status` -> the org's calendar-delegation state. */
export function getOrgCalendarStatus(): Promise<ApiResult<OrgCalendarStatus>> {
    return request<OrgCalendarStatus>('GET', '/api/calendars/status');
}

/** `GET /api/calendars/ms/consent-url` -> { url } to redirect the admin to for Entra consent. */
export function getMsConsentUrl(): Promise<ApiResult<{ url: string }>> {
    return request<{ url: string }>('GET', '/api/calendars/ms/consent-url');
}

/** `POST /api/calendars/google/enroll` -> enroll Google DWD for a durable admin subject. */
export function enrollGoogleCalendar(adminSubject: string): Promise<ApiResult<{ ok: boolean }>> {
    return request<{ ok: boolean }>('POST', '/api/calendars/google/enroll', { admin_subject: adminSubject });
}

// --- Health (status board + alerts) -----------------------------------------

const q = (tenant: string): string => `tenant=${encodeURIComponent(tenant)}`;

export function getHealthSummary(tenant: string): Promise<ApiResult<HealthSummaryResponse>> {
    return request<HealthSummaryResponse>('GET', `/api/health/summary?${q(tenant)}`);
}

export function listAlerts(tenant: string, includeAcknowledged = false): Promise<ApiResult<ListAlertsResponse>> {
    const inc = includeAcknowledged ? '&includeAcknowledged=true' : '';

    return request<ListAlertsResponse>('GET', `/api/health/alerts?${q(tenant)}${inc}`);
}

export function ackAlert(tenant: string, alertId: string): Promise<ApiResult<AcknowledgeAlertResponse>> {
    return request<AcknowledgeAlertResponse>('POST', `/api/health/alerts/${encodeURIComponent(alertId)}/ack?${q(tenant)}`);
}

export function rescan(tenant: string): Promise<ApiResult<ScanResultResponse>> {
    return request<ScanResultResponse>('POST', `/api/health/scan?${q(tenant)}`);
}

export function getRoomHealth(id: string, tenant: string): Promise<ApiResult<RoomHealthResponse>> {
    return request<RoomHealthResponse>('GET', `/api/rooms/${encodeURIComponent(id)}/health?${q(tenant)}`);
}

export function previewLink(id: string, target: 'tv' | 'remote'): Promise<ApiResult<PreviewLinkResponse>> {
    return request<PreviewLinkResponse>('POST', `/api/rooms/${encodeURIComponent(id)}/preview/${target}`);
}
